import classNames from "classnames";
import { isArray } from "lodash";
import Link from "next/link";
import { FC, Fragment, useEffect, useRef, useState } from "react";

import { convertToSlug } from "~/utils/converters";
import Accordion from "../Accordion";
import Text from "../Text";
import styles from "./TableOfContent.module.scss";

interface TableOfContentProps {
  headings?: string[];
  className?: string;
}

const TableOfContent: FC<TableOfContentProps> = ({ headings, className }) => {
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const headingRefs = useRef<Array<HTMLHeadingElement | null>>([]);
  const isUserClick = useRef<boolean>(false);

  useEffect(() => {
    const h2Elements =
      typeof window !== "undefined" ? document.querySelectorAll("h2") : [];

    headingRefs.current = Array.from(h2Elements);
    headingRefs.current.forEach((elm, index) => {
      elm?.setAttribute("id", `${convertToSlug(elm.textContent)}-${index + 1}`);
    });

    const headingSlugs = headings?.map(
      (heading, index) => `${convertToSlug(heading)}-${index + 1}`
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (
            entry.isIntersecting &&
            !isUserClick.current &&
            headingSlugs?.includes(entry.target.id)
          ) {
            setActiveHeading(entry.target.id);
          }
        });
      },
      {
        threshold: 0.5,
        rootMargin: "-10% 0% -35% 0px",
      }
    );

    headingRefs.current.forEach((heading) => {
      if (heading) {
        observer.observe(heading);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [headings]);

  const handleHeadingClick = (item: string, index: number) => {
    const convertedSlug = `${convertToSlug(item)}-${index + 1}`;

    setActiveHeading(convertedSlug);
    isUserClick.current = true;
    const targetElement = headingRefs.current.find(
      (heading) => heading?.textContent === convertedSlug
    );

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth" , inline:"center"});
    }

    // Reset the user click flag after a short delay
    setTimeout(() => {
      isUserClick.current = false;
    }, 500);
  };

  return (
    <Fragment>
      <div className={classNames(className, styles.table_of_content)}>
        <Text type="title-4" className="text-grey-900">
          Table of Content
        </Text>
        {isArray(headings) && headings.length > 0 ? (
          <div className={styles.content_wrapper}>
            {headings.map((item, index) => (
              <Link
                href={`#${convertToSlug(item)}-${index + 1}`}
                key={index}
                onClick={() => handleHeadingClick(item, index)}
              >
                <Text
                  className={classNames(styles.content, {
                    [styles.active]:
                      `${convertToSlug(item)}-${index + 1}` === activeHeading,
                  })}
                >
                  {/* <span className="min-w-[30px]"> //Off index
                    {index + 1 < 10 ? "0" : ""}
                    {index + 1}.
                  </span> */}
                  <span className="line-clamp-4">{item}</span>
                </Text>
              </Link>
            ))}
          </div>
        ) : (
          <div className="px-4 py-3">No available content</div>
        )}
      </div>
      <Accordion
        className="lg:!hidden order-[-1]"
        items={[
          {
            label: "Table of Content",
            key: "table-of-content",
            content:
              isArray(headings) && headings.length > 0 ? (
                <div className={styles.content_wrapper}>
                  {headings.map((item, index) => (
                    <Link
                      href={`#${convertToSlug(item)}-${index + 1}`}
                      key={index}
                      onClick={() => handleHeadingClick(item, index)}
                    >
                      <Text
                        className={classNames(styles.content, {
                          [styles.active]:
                            `${convertToSlug(item)}-${index + 1}` ===
                            activeHeading,
                        })}
                      >
                        <span className="min-w-[30px]">
                          {index + 1 < 10 ? "0" : ""}
                          {index + 1}.
                        </span>
                        <span className="line-clamp-2">{item}</span>
                      </Text>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="px-4 py-3">No available content</div>
              ),
          },
        ]}
      />
    </Fragment>
  );
};

export default TableOfContent;
